/**
 * Solana configuration — USDC mint addresses per network.
 */

import type { Network } from '@/lib/network';
import { getActiveNetwork } from '@/lib/network/active';
import { deploymentEnv } from '@/lib/network/config';

const DEVNET_USDC_MINT = 'GwMYubfhtHzP2vNcoDVRbt64T5ruHRpq5w2wCXGMNTB7';
const MAINNET_USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';

// Each network's quote mint comes from `NEXT_PUBLIC_QUOTE_MINT_<NETWORK>`,
// falling back to the mint the network has always traded in. `local` inherits
// devnet's while it runs devnet's program (see `deploymentEnv`).
function quoteMint(network: Network): string {
  return (
    deploymentEnv(network, 'QUOTE_MINT') ??
    (network === 'mainnet' ? MAINNET_USDC_MINT : DEVNET_USDC_MINT)
  );
}

const USDC_MINT: Record<Network, string> = {
  local: quoteMint('local'),
  devnet: quoteMint('devnet'),
  mainnet: quoteMint('mainnet'),
};

/** True when a devnet-side network trades the default devnet USDC because
 *  `NEXT_PUBLIC_QUOTE_MINT_<NETWORK>` is unset. Mainnet's default is the
 *  canonical USDC mint, so it never counts as a fallback. */
export function fallsBackToDevnetUsdc(network: Network): boolean {
  return network !== 'mainnet' && deploymentEnv(network, 'QUOTE_MINT') === undefined;
}

/** Returns the USDC mint address for the requested or currently active network. */
export function getUsdcMint(network: Network = getActiveNetwork()): string {
  return USDC_MINT[network];
}

export { USDC_MINT };

/** USDC amounts below this (display units) are treated as zero ("dust"). */
export const USDC_DUST_THRESHOLD: number = 0.02;
/** Same threshold in D6 encoding (20_000 = $0.02). */
export const USDC_DUST_THRESHOLD_D6: number = 20_000;

/** USDC D6 scale factor (1 USDC = 10^6 base units). Use this everywhere instead of the literal 1_000_000. */
export const D6_SCALE: number = 1_000_000;

/** D9 scale factor for prices and unitless market/conviction shares. Outcome-token amounts are D6. */
export const D9_SCALE: number = 1_000_000_000;
